import React from "react";
import { FileText, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";

export const TYPE_LABELS = { text: "Texte", email: "Email", phone: "Téléphone", whatsapp: "N° WhatsApp", number: "Nombre", amount: "Montant (€)", date: "Date",
  bool: "Oui / non", choice: "Liste de choix", file: "Fichier (PDF / image)", link: "Lien vers une famille" };

export const inputCls = "w-full px-2.5 py-1.5 text-sm border border-norea-border rounded bg-white focus:ring-1 focus:ring-norea-primary outline-none";

export function display(col, v) {
  if (v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length)) return "—";
  if (col.type === "bool") return v ? "Oui" : "Non";
  if (col.type === "amount") return `${Number(v).toLocaleString("fr-FR")} €`;
  if (col.type === "date") return new Date(v).toLocaleDateString("fr-FR");
  if (col.type === "file") return v.filename;
  const lab = col.option_labels || {};
  if (col.type === "choice") return [].concat(v).map((x) => lab[x] || x).join(", ");
  return Array.isArray(v) ? v.join(", ") : String(v);
}

export function openFile(fid, rid, cid) {
  const win = window.open("", "_blank");
  fetch(`${API}/families/${fid}/rows/${rid}/files/${cid}`, { headers: { Authorization: `Bearer ${localStorage.getItem("norea_token")}` } })
    .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
    .then((b) => { if (win) win.location.href = URL.createObjectURL(b); })
    .catch(() => { win?.close(); toast.error("Fichier introuvable"); });
}

export function FileCell({ family, row, col, onDone }) {
  const v = row?.values[col.id];
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      if (family.kind === "materiel") await api.post(`/materiel-pv/${row.values.type === "Panneau" ? "panneaux" : "onduleurs"}/${row.id}/fiche`, fd);
      else await api.post(`/families/${family.id}/rows/${row.id}/files/${col.id}`, fd);
      toast.success("Fichier enregistré");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
  };
  if (!row) return <span className="text-[11px] text-norea-textMuted">Enregistrez d'abord la ligne</span>;
  return (
    <span className="inline-flex items-center gap-2">
      {v?.filename && <button type="button" onClick={() => openFile(family.id, row.id, col.id)} className="text-xs text-norea-primary hover:underline inline-flex items-center gap-1" data-testid={`file-open-${col.id}`}><FileText className="w-3 h-3" />{v.filename}</button>}
      <label className="text-xs px-2 py-1 rounded border border-norea-border cursor-pointer hover:bg-norea-surfaceHover inline-flex items-center gap-1" data-testid={`file-upload-${col.id}`}>
        <Upload className="w-3 h-3" /> {v?.filename ? "Remplacer" : "Ajouter"}
        <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
      </label>
    </span>
  );
}

export function FieldInput({ col, value, onChange, families, testid }) {
  if (col.type === "bool") return <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />;
  if (col.type === "choice" && col.multiple) {
    const cur = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {col.options.map((o) => <label key={o} className="text-xs inline-flex items-center gap-1"><input type="checkbox" checked={cur.includes(o)} onChange={(e) => onChange(e.target.checked ? [...cur, o] : cur.filter((x) => x !== o))} />{o}</label>)}
      </div>
    );
  }
  if (col.type === "choice") return <select value={value || ""} onChange={(e) => onChange(e.target.value || null)} className={inputCls} data-testid={testid}><option value="">—</option>{col.options.map((o) => <option key={o} value={o}>{(col.option_labels || {})[o] || o}</option>)}</select>;
  if (col.type === "link") {
    const names = families.find((f) => f.id === col.link_family)?.names || [];
    return <select value={value || ""} onChange={(e) => onChange(e.target.value || null)} className={inputCls} data-testid={testid}><option value="">—</option>{names.map((o) => <option key={o}>{o}</option>)}</select>;
  }
  if (col.type === "email" && col.multiple) return <EmailChips value={value} onChange={onChange} testid={testid} />;
  const text = Array.isArray(value) ? value.join("; ") : value ?? "";
  const type = { number: "number", amount: "number", date: "date", email: col.multiple ? "text" : "email", phone: "tel", whatsapp: "tel" }[col.type] || "text";
  return <input type={type} value={text} onChange={(e) => onChange(e.target.value)} className={inputCls} placeholder={col.multiple ? "séparez par ;" : ""} data-testid={testid} />;
}

function EmailChips({ value, onChange, testid }) {
  const list = Array.isArray(value) ? value : value ? String(value).split(/[;,]/).map((x) => x.trim()).filter(Boolean) : [];
  const [draft, setDraft] = React.useState("");
  const add = () => {
    const items = draft.split(/[\s;,]+/).map((x) => x.trim().toLowerCase()).filter((x) => x.includes("@") && !list.includes(x));
    if (items.length) onChange([...list, ...items]);
    setDraft("");
  };
  return (
    <div className="flex flex-wrap items-center gap-1 px-1.5 py-1 border border-norea-border rounded bg-white min-w-[220px]" data-testid={testid} onClick={(ev) => ev.preventDefault()}>
      {list.map((e) => (
        <span key={e} className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-norea-bg border border-norea-border">{e}
          <button type="button" onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); onChange(list.filter((x) => x !== e)); }} className="text-norea-textMuted hover:text-red-700" aria-label={`Retirer ${e}`} data-testid={`${testid}-remove-${e}`}>×</button></span>
      ))}
      <input value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={add} onKeyDown={(e) => { if (["Enter", ",", ";", " "].includes(e.key)) { e.preventDefault(); add(); } }}
        placeholder={list.length ? "+ email" : "email puis Entrée"} className="flex-1 min-w-[110px] text-sm outline-none px-1 py-0.5" data-testid={`${testid}-input`} />
    </div>
  );
}
